import { useGetAllFeatureFlags } from "@/contexts/FeatureFlagContext"
import { questionAnsweringTaskGroupApiCalls } from "@/services/api/questionAnsweringTaskGroupService"
import { useAutofillQuestionnaire } from "@/services/api/questionnaireService"
import {
  Questionnaire,
  QuestionnaireStatus,
} from "@/types/questionnaire/models"
import { getRouteByStatus } from "@/utils/questionnaire/questionnaireStatus"
import { useQueryClient } from "@tanstack/react-query"
import { NavigateFunction } from "react-router"

interface RunAutofillParams {
  questionnaireId?: string
  questionnaire?: Questionnaire
  navigate: NavigateFunction
  refetchQuestionnaire: () => Promise<{ data?: Questionnaire }>
}

export const useQuestionnaireAutofill = ({
  questionnaireId,
  questionnaire,
  navigate,
  refetchQuestionnaire,
}: RunAutofillParams) => {
  const queryClient = useQueryClient()
  const featureFlags = useGetAllFeatureFlags()
  const { mutateAsync: triggerAutofillAnswersAsync, isPending } =
    useAutofillQuestionnaire()

  const clearQuestionnaireCache = () => {
    queryClient.removeQueries({
      queryKey: ["questionnaire", { questionnaireId }],
    })
  }

  const runAutofillWithRedirect = async (useCompanyProfile: boolean = true) => {
    if (!questionnaireId || !questionnaire) {
      throw new Error("Questionnaire context is missing")
    }

    const latestActiveTaskGroups =
      await questionAnsweringTaskGroupApiCalls.getTaskGroups(
        questionnaireId,
        true,
      )

    const existingActiveTaskGroup = latestActiveTaskGroups?.[0]

    if (existingActiveTaskGroup) {
      clearQuestionnaireCache()

      navigate(
        getRouteByStatus(
          questionnaireId,
          QuestionnaireStatus.GeneratingAnswers,
          questionnaire.fileName,
          questionnaire.hasPositionData,
          existingActiveTaskGroup.id,
          featureFlags,
        ),
        { replace: true },
      )
      return
    }

    const autoFillTrigger = await triggerAutofillAnswersAsync({
      questionnaireId,
      useCompanyProfile,
    })

    const refetchedQuestionnaire = await refetchQuestionnaire()
    const nextStatus = autoFillTrigger?.id
      ? QuestionnaireStatus.GeneratingAnswers
      : refetchedQuestionnaire.data?.status || questionnaire.status

    const actualRoute = getRouteByStatus(
      refetchedQuestionnaire.data?.id || questionnaireId,
      nextStatus,
      refetchedQuestionnaire.data?.fileName || questionnaire.fileName,
      refetchedQuestionnaire.data?.hasPositionData ??
        questionnaire.hasPositionData,
      autoFillTrigger?.id,
      featureFlags,
    )

    clearQuestionnaireCache()

    navigate(actualRoute, {
      replace: true,
    })
    return
  }

  return {
    runAutofillWithRedirect,
    isPending,
  }
}
